import type { ConsoleBrandDetail } from '@rsecure/brand-contracts/console';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Route, Routes, useParams } from 'react-router';
import {
  Badge,
  CopyButton,
  Empty,
  ErrorState,
  Loading,
  Ltr,
  Notice,
  StatusBadge,
  Time,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { AssetsTab, LogoStage } from './brand/Assets';
import { HistoryTab } from './brand/History';
import { OverviewTab } from './brand/Overview';
import { PreviewTab } from './brand/Preview';
import { ProvenanceTab } from './brand/Provenance';
import { BrandReviewsTab } from './brand/Reviews';
import { TimelineTab } from './brand/Timeline';

export function BrandPage() {
  const { ref = '' } = useParams();
  const q = useQuery({ queryKey: ['brand', ref], queryFn: () => api.brand(ref) });
  if (q.isError)
    return q.error instanceof ApiError && q.error.status === 404 ? (
      <div className="card">
        <Empty
          title="Brand not found"
          sub={`No entity has the id, slug or retired slug “${ref}”.`}
        />
      </div>
    ) : (
      <ErrorState error={q.error} onRetry={() => q.refetch()} />
    );
  if (!q.data) return <Loading rows={12} />;
  return <BrandView d={q.data} />;
}

function BrandView({ d }: { d: ConsoleBrandDetail }) {
  const e = d.entity;
  const primary = d.domains.find((x) => x.role === 'primary');
  const openReviews = d.reviews.filter((r) => r.status === 'OPEN');
  const blocking = openReviews.filter((r) => r.blocking);
  const onLight = d.roleCoverage.find((r) => r.role === 'logo_on_light');
  const onDark = d.roleCoverage.find((r) => r.role === 'logo_on_dark');
  const base = `/brands/${e.slug}`;
  const tabs = [
    { to: base, label: 'Overview', end: true },
    { to: `${base}/assets`, label: 'Visual assets', n: d.assets.length },
    { to: `${base}/provenance`, label: 'Provenance' },
    { to: `${base}/history`, label: 'Revisions', n: d.revisions.length },
    { to: `${base}/timeline`, label: 'ETL timeline' },
    { to: `${base}/reviews`, label: 'Reviews', n: openReviews.length },
    { to: `${base}/preview`, label: 'Campaign appearance' },
  ];
  return (
    <>
      <nav aria-label="Breadcrumb" className="muted" style={{ fontSize: 13, marginBottom: 10 }}>
        <Link className="link" to="/">
          Catalog
        </Link>{' '}
        / <span>{e.canonicalName}</span>
      </nav>
      <section className="brand-hero">
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <h1>{e.canonicalName}</h1>
            <StatusBadge kind="pipeline" value={e.pipelineState} />
            {d.published ? (
              <Badge tone="ok" title="Active published revision">
                revision #{d.published.revision.number} live
              </Badge>
            ) : (
              <Badge tone="warn">Not published</Badge>
            )}
            {blocking.length > 0 && <Badge tone="bad">{blocking.length} blocking review(s)</Badge>}
          </div>
          {e.nameHe && (
            <div className="he" dir="auto">
              {e.nameHe}
            </div>
          )}
          <div className="meta">
            <span className="idline" title="Stable canonical brand ID">
              ID <Ltr mono>{e.id}</Ltr>
              <CopyButton value={e.id} label="brand ID" />
            </span>
            <span className="idline">
              slug <Ltr mono>{e.slug}</Ltr>
              <CopyButton value={e.slug} label="slug" />
            </span>
            {primary && (
              <span className="idline">
                <Ltr>{primary.domain}</Ltr>
                <Badge tone={primary.verification === 'verified' ? 'ok' : 'warn'}>
                  {primary.verification}
                </Badge>
              </span>
            )}
            <span>{e.kind}</span>
            <span>
              {e.category ?? '—'} · {e.region}
            </span>
            <span>
              updated <Time iso={e.updatedAt} relative />
            </span>
          </div>
          {!d.published && (
            <div style={{ marginTop: 14 }}>
              <Notice tone="warn">
                <strong>Not published.</strong> This entity has no active published revision. It is
                not served to campaigns.
                {blocking.length > 0 && <> Blocked by: {blocking.map((r) => r.kind).join(', ')}.</>}
              </Notice>
            </div>
          )}
        </div>
        <LogoStage
          name={e.canonicalName}
          light={onLight?.publicUrl ?? null}
          dark={onDark?.publicUrl ?? null}
          compact
        />
      </section>
      <nav className="tabs" aria-label="Brand sections">
        {tabs.map((tb) => (
          <NavLink key={tb.to} to={tb.to} end={tb.end}>
            {tb.label}
            {tb.n ? <span className="n">{tb.n}</span> : null}
          </NavLink>
        ))}
      </nav>
      <Routes>
        <Route index element={<OverviewTab d={d} />} />
        <Route path="assets" element={<AssetsTab d={d} />} />
        <Route path="provenance" element={<ProvenanceTab d={d} />} />
        <Route path="history" element={<HistoryTab d={d} />} />
        <Route path="timeline" element={<TimelineTab d={d} />} />
        <Route path="reviews" element={<BrandReviewsTab d={d} />} />
        <Route path="preview" element={<PreviewTab d={d} />} />
      </Routes>
    </>
  );
}
